import ReleaseFlag from 'api/legacy/releaseFlags/enumReleaseFlags';
import useSimDetails from 'api/sims/useSimDetails';
import useIsEuiccSim from 'hooks/SimDetails/useIsEuiccSim';
import useUserInfo from 'hooks/User/useUserInfo';
import { useCallback, useMemo } from 'react';
import type { DropdownItemEntry, ManageSimItemHook } from '../types';

const useSwitchProfiles: ManageSimItemHook = ({
  context,
  selectedSims,
  handleModalOpen,
  onActionTaken,
  onSimsModified,
  disabled,
}) => {
  const onClick = useCallback(() => {
    handleModalOpen({
      modalName: 'SimInventorySwitchProfilesModal',
      modalProps: { sims: selectedSims, onModalSuccess: onSimsModified },
    });
    onActionTaken();
  }, [onActionTaken, handleModalOpen, onSimsModified, selectedSims]);

  const simId = context === 'details' && selectedSims ? [...selectedSims.keys()][0] : undefined;
  const { simDetails } = useSimDetails(simId);

  const isEuiccSim = useIsEuiccSim(simId);

  const { organizationRole, releaseFlags } = useUserInfo();

  const profileSwapAllowed = releaseFlags.has(ReleaseFlag.MANAGE_SINGLE_SIM_PROFILE_SWAP);

  const show =
    profileSwapAllowed &&
    context === 'details' &&
    isEuiccSim &&
    !!simDetails.fallbackInstalled &&
    !!simDetails.fallbackEnabled &&
    (organizationRole === 'manager' || organizationRole === 'admin') &&
    (simDetails.profilesInstalledOnCard ?? 0) > 1;

  // We will show the action if it meets the criteria above,
  // but it will be disabled if the sim is not in a swappable state
  const isSwappableState = ['LIVE', 'TEST-ACTIVATE', 'FACTORY'].includes(
    simDetails.networkStatus ?? ''
  );

  return useMemo<DropdownItemEntry | false>(
    () =>
      show && {
        children: 'Switch profiles...',
        iconStart: 'SwapSIM',
        disabled: disabled || !isSwappableState,
        onClick,
        id: `sim${context}-dataplan-switchprofiles`,
      },
    [context, disabled, onClick, show, isSwappableState]
  );
};

export default useSwitchProfiles;
